import AnimatedLoader from "../../../motionKit/AnimatedLoader";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Accordion from "../../../components/shared/Accordion";
import EmailThread from "../../../components/shared/EmailThread";
import AiMessageAssistPopover from "../../../components/shared/AiMessageAssistPopover";
import { fetchEmailThread, replyToEmailThread, registerWatchedThread, updateSavedAgentFinalRate, getListOfTemplates, emailTemplatePopulated, getAdminUsers, saveDraftEmail, addAdmins, sendEmail, parseAgentRateFromInput, postQuoteMessageAssist } from "../../../Api";
import { shallowEqual, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { formatMissingFieldLabel } from "../../../Helper/FilterationFunctions";
import { formatTemplateOptionLabel } from "../../../Helper/emailDisplayUtils";
import { highlightedPayable, renderCostBreakdown } from "./AirlineConversation";
import { showSwal } from "../../../Helper/ShowAlert";
import { TRANSPORTER } from "../../../Helper/partnerLabels";
import TemplateSwitchConfirmModal from "../../../components/shared/TemplateSwitchConfirmModal";
import { EmailThreadSkeleton } from "../../../components/shared/SkeletonComponents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faTimes,
  faEdit,
  faSave,
  faPlus,
  faTrash,
  faPlane,
  faTruck,
  faCircleNotch,
  faRotateRight,
  faCheck,
} from "@fortawesome/free-solid-svg-icons"; /* faTimes still used by add-row controls in FinalRateSection (lines ~518, 694) */
import { AnimatePresence, motion } from "framer-motion";
import toast from 'react-hot-toast';
import { stripHtmlToText } from "../../../Helper/aiEmailParseHelpers";
import { sortThreadMessages } from "./threadOrdering";

// Helper function to parse email addresses from headers
const parseEmailAddresses = (headerValue) => {
  if (!headerValue) return [];
  return headerValue
    .split(',')
    .map(addr => {
      const trimmed = addr.trim();
      if (trimmed.includes("<")) {
        return trimmed.split("<")[1].replace(">", "").trim();
      }
      return trimmed;
    })
    .filter(addr => addr && addr.length > 0);
};

const parseEmailMessage = (message) => {
  const fromHeader = message.headers.from || "";
  const toHeader = message.headers.to || "";
  const ccHeader = message.headers.cc || "";
  const bccHeader = message.headers.bcc || "";

  // Keep the raw ms epoch alongside the display string. combinedTransportMessages
  // sorts by this, and without it the comparator falls through to parsing the
  // localized `date` string (locale-dependent → NaN in some builds), which
  // makes new SSE messages land in the wrong position.
  const internalMs = parseInt(message.internalDate, 10);

  return {
    sender: fromHeader,
    email: fromHeader.includes("<")
      ? fromHeader.split("<")[1].replace(">", "")
      : fromHeader,
    to: parseEmailAddresses(toHeader),
    cc: parseEmailAddresses(ccHeader),
    bcc: parseEmailAddresses(bccHeader),
    subject: message.headers.subject,
    date: new Date(internalMs).toLocaleString(),
    // null, not 0: 0 is finite, so it passed the old sort guard and pinned the
    // message to epoch 1970 — the top of the thread. See threadOrdering.js.
    internalDate: Number.isFinite(internalMs) ? internalMs : null,
    html: message.body,
    messageId: message.headers.messageId,
    id: message.id,
    attachments: message.attachments || [],
  };
};

/* Every rate input is exactly RATE_FIELD_H tall so the unit chip can be centred
   against it with a matching-height flex box (see RateEditorField). Keep the two
   in sync — that pairing is what stops the chip drifting off the text baseline. */
const RATE_FIELD_H = "h-10";

const RATE_EDITOR_INPUT_CLASS = `w-full ${RATE_FIELD_H} rounded-xl border border-slate-200 bg-white px-3.5 pr-16 py-2 text-[13.5px] font-semibold tabular-nums text-slate-800 outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-200/60 placeholder:font-normal placeholder:text-slate-400 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-white disabled:text-slate-800 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.6)] dark:text-slate-100 dark:focus:border-blue-500 dark:disabled:border-[oklch(0.3_0.02_264)] dark:disabled:bg-[oklch(0.26_0.028_264/0.35)] dark:disabled:text-slate-300`;

const RATE_MONO_FONT = '"IBM Plex Mono", ui-monospace, monospace';

/* Design-language rate field: "Final Rate (£)" renders as a mono uppercase
   eyebrow label + the unit as a suffix chip INSIDE the input (right edge),
   like the generated rate-form design — not baked into the label text. */
function RateEditorField({ title, className = "", children }) {
  const m = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(title || "");
  const label = m ? m[1] : title;
  const unit = m ? m[2] : null;
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="block text-[12.5px] font-bold text-slate-800 dark:text-slate-100">
        {label}
      </label>
      {/* The positioning context deliberately carries NO space-y. Tailwind's
          space-y-* applies `margin-top` to every child after the first via
          `> :not([hidden]) ~ :not([hidden])`, and margin still displaces an
          absolutely-positioned element even at top-0 — so while the chip lived
          inside the spaced container it sat ~6px below centre in every field.
          The inputs keep their spacing in their own wrapper; the chip sits
          outside it. */}
      <div className="relative">
        <div className="space-y-1.5">{children}</div>
        {unit ? (
          /* Centre the chip against the FIRST input by matching its height and
             using flex, instead of a magic top offset. The old `top-[21px]
             -translate-y-1/2` was tuned to one input's half-height, so it sat
             below the text baseline and — where a field stacks two inputs
             (screening / war-risk / handling all have a "minimum" row) — it
             drifted onto the first input's bottom border. */
          <span
            aria-hidden
            data-testid="rate-unit-chip"
            className={`pointer-events-none absolute right-2.5 top-0 flex ${RATE_FIELD_H} items-center`}
          >
            <span
              className="rounded-md border border-slate-200/80 bg-slate-50 px-1.5 py-0.5 text-[10.5px] font-semibold leading-none text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400"
              style={{ fontFamily: RATE_MONO_FONT }}
            >
              {unit}
            </span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* Does this saved partner actually carry a rate, or has nobody touched it?
   Answered from the SAVED RECORD, where an absent charge is absent — the
   display state coerces every one through `|| 0` before it reaches an input.
   A rate of 0 counts as nothing entered: the product has no notion of a genuine
   zero-cost haulage leg, and treating one as "Saved" is the bug being fixed. */
export function recordHasRates(record) {
  if (!record) return false;
  const stated = v => v !== null && v !== undefined && v !== "" && Number(v) > 0;
  const anyIn = v =>
    v && typeof v === "object"
      ? Object.values(v).some(anyIn)
      : stated(v);
  return (
    stated(record.finalRate) ||
    stated(record.minCharge) ||
    anyIn(record.screeningPrices) ||
    anyIn(record.FFWH)
  );
}

export function FinalRateSection({
  selectedAgentId,
  isEditing,
  openRates,
  isSavable,
  setIsEditing,
  finalRates,
  setFinalRates,
  quoteId,
  token,
  setRefetchTrigger,
  setFqctTrigger = setRefetchTrigger,
  isAirline,
  updateApi,
  role,
  /* "Is `Saved` saved?" — an untouched card showed a green tick and the word
     Saved while every field on it read "Not set", so an operator could not tell
     a card they had filled in from one nobody had touched. It cannot be asked of
     `finalRates`: that seeds every absent rate through `|| 0`, so by the time a
     figure reaches these inputs an untouched card and one genuinely rated at
     zero are identical. Only the saved record still knows, so the parent — which
     holds it — answers. */
  hasSavedRates = false,
}) {
  const [collapsedStates, setCollapsedStates] = useState(openRates || {}); // Track collapse state per rate entry
  const agentRatesRaw = finalRates[selectedAgentId];
  const agentRates = agentRatesRaw?.screeningPrices ? [{ ...agentRatesRaw, ...agentRatesRaw.screeningPrices[0], screeningPrices: undefined }, ...agentRatesRaw.screeningPrices.slice(1)] : [{
    ...agentRatesRaw
  }];
  const hasSelection = Boolean(selectedAgentId);
  const activeRateCount = hasSelection ? agentRates.length : 0;
  const hasAnyRateEntered = hasSelection && hasSavedRates;

  const [rateErrors, setRateErrors] = useState({});

  const validateField = (field, newValue, rateAtIndex) => {
    const num = Number(newValue);
    if (newValue !== "" && newValue !== null && newValue !== undefined) {
      if (num < 0) return "Must be ≥ 0";
    }
    if (field === "Fuel") {
      if (newValue !== "" && newValue !== null && (num < 0 || num > 100))
        return "Must be between 0–100%";
    }
    if (field === "minCurrentRate") {
      const freight = Number(rateAtIndex?.currentRate ?? 0);
      if (freight > 0 && num > 0 && num < freight * 2)
        return `Min charge must be ≥ 2× freight rate (${freight * 2})`;
    }
    if (field === "minPrimaryScreening") {
      const rate = Number(rateAtIndex?.PrimaryScreening ?? 0);
      if (rate > 0 && num > 0 && num < rate * 2)
        return `Min must be ≥ 2× primary screening rate (${rate * 2})`;
    }
    if (field === "minSecondaryScreening") {
      const rate = Number(rateAtIndex?.SecondaryScreening ?? 0);
      if (rate > 0 && num > 0 && num < rate * 2)
        return `Min must be ≥ 2× secondary screening rate (${rate * 2})`;
    }
    if (field === "minWar") {
      const rate = Number(rateAtIndex?.War ?? 0);
      if (rate > 0 && num > 0 && num < rate * 2)
        return `Min must be ≥ 2× war risk surcharge rate (${rate * 2})`;
    }
    if (field === "minHandling") {
      const rate = Number(rateAtIndex?.Handling ?? 0);
      if (rate > 0 && num > 0 && num < rate * 2)
        return `Min must be ≥ 2× handling fee rate (${rate * 2})`;
    }
    return "";
  };

  const setFieldError = (index, field, msg) => {
    setRateErrors((prev) => {
      const agentErrors = { ...(prev[selectedAgentId] || {}) };
      const indexErrors = { ...(agentErrors[index] || {}) };
      if (msg) indexErrors[field] = msg;
      else delete indexErrors[field];
      if (Object.keys(indexErrors).length === 0) delete agentErrors[index];
      else agentErrors[index] = indexErrors;
      return { ...prev, [selectedAgentId]: agentErrors };
    });
  };

  const getError = (index, field) => rateErrors[selectedAgentId]?.[index]?.[field] || "";

  const hasAnyErrors = () => {
    const agentErrs = rateErrors[selectedAgentId] || {};
    return Object.values(agentErrs).some(
      (indexErrs) => typeof indexErrs === "object" && Object.values(indexErrs).some(Boolean)
    );
  };

  const inputCls = (index, field) => {
    // Design: in view mode an EMPTY field reads "Not set" behind a dashed
    // border, so filled vs missing scans instantly.
    const v = agentRates?.[index]?.[field];
    const dashed =
      !isEditing[selectedAgentId] && (v === undefined || v === null || v === "")
        ? " border-dashed"
        : "";
    if (!getError(index, field)) return RATE_EDITOR_INPUT_CLASS + dashed;
    return (RATE_EDITOR_INPUT_CLASS + dashed)
      .replace("border-slate-200", "border-red-300")
      .replace("dark:border-[oklch(0.33_0.022_264)]", "dark:border-red-500");
  };

  // Handle rate change
  const handleRateChange = (e, field, index, airportIndex = null) => {
    const newValue = e.target.value;
    const VALIDATED_FIELDS = [
      "currentRate", "minCurrentRate", "Fuel",
      "War", "minWar", "Handling", "minHandling",
      "PrimaryScreening", "minPrimaryScreening",
      "SecondaryScreening", "minSecondaryScreening",
    ];
    if (VALIDATED_FIELDS.includes(field)) {
      const existing = finalRates[selectedAgentId];
      const rateAtIndex = Array.isArray(existing?.screeningPrices)
        ? { ...existing, ...existing.screeningPrices[index] }
        : existing;
      setFieldError(index, field, validateField(field, newValue, rateAtIndex));
    }
    setFinalRates((prev) => {
      const existing = prev[selectedAgentId];

      // Handle OriginAirports array updates
      if (field === "OriginAirports" && airportIndex !== null) {
        const updatedOriginAirports = [...(Array.isArray(existing?.OriginAirports) ? existing.OriginAirports : [])];
        updatedOriginAirports[airportIndex] = e.target.value;
        return {
          ...prev,
          [selectedAgentId]: { ...existing, OriginAirports: updatedOriginAirports },
        };
      }

      // Handle screeningPrices array updates
      if (Array.isArray(existing?.screeningPrices)) {
        const updatedScreeningPrices = [...existing.screeningPrices];
        updatedScreeningPrices[index] = { ...updatedScreeningPrices[index], [field]: e.target.value };
        return { ...prev, [selectedAgentId]: { ...existing, screeningPrices: updatedScreeningPrices } };
      } else {
        return {
          ...prev,
          [selectedAgentId]: { ...existing, [field]: e.target.value },
        };
      }
    });
  };

  // Save all (with nested screeningPrices support)
  const handleSaveAll = async () => {
    const agentData = agentRatesRaw || {};
    const { currentRate, minCurrentRate, screeningPrices = [] } = agentData;

    // If there’s no screeningPrices, we treat top-level fields as one destination
    const destinations = screeningPrices.length
      ? screeningPrices
      : [
        {
          PrimaryScreening: Number(agentData.PrimaryScreening) || 0,
          minPrimaryScreening: Number(agentData.minPrimaryScreening) || 0,
          SecondaryScreening: Number(agentData.SecondaryScreening) || 0,
          minSecondaryScreening: Number(agentData.minSecondaryScreening) || 0,
          DestinationAirport: agentData.DestinationAirport || "",
        },
      ];

    const payload = {
      token,
      quoteId,
      role: role,
      savedAgentToUpdate: selectedAgentId,
      finalRate: Number(!isAirline ? (screeningPrices[0]?.currentRate ?? currentRate) : currentRate) || 0,
      minCharge: Number(!isAirline ? (screeningPrices[0]?.minCurrentRate ?? minCurrentRate) : minCurrentRate) || 0,
      screeningPrices: destinations.map((d) => ({
        primaryScreeningPrice: {
          ratePerVolumeWeight: Number(d.PrimaryScreening) || 0,
          minCharge: Number(d.minPrimaryScreening) || 0,
        },
        secondaryScreeningPrice: {
          ratePerVolumeWeight: Number(d.SecondaryScreening) || 0,
          minCharge: Number(d.minSecondaryScreening) || 0,
        },
        // PrimaryScreening: Number(d.PrimaryScreening) || 0,
        // minPrimaryScreening: Number(d.minPrimaryScreening) || 0,
        // SecondaryScreening: Number(d.SecondaryScreening) || 0,
        // minSecondaryScreening: Number(d.minSecondaryScreening) || 0,
        destinationAirport: d.DestinationAirport || "",
      })),
      ...(isAirline
        ? {
          FFWH: {
            fuelSurcharge: Number(agentData.Fuel) || 0,
            freightCharge: {
              ratePerVolumeWeight: Number(agentData.Freight) || 0,
              minCharge: Number(agentData.minCurrentRate) || 0,
            },
            warRiskSurcharge: {
              ratePerVolumeWeight: Number(agentData.War) || 0,
              minCharge: Number(agentData.minWar) || 0,
            },
            handlingFee: {
              ratePerVolumeWeight: Number(agentData.Handling) || 0,
              minCharge: Number(agentData.minHandling) || 0,
            },
          },
          originAirports: Array.isArray(agentData.OriginAirports) ? agentData.OriginAirports.filter(airport => airport && airport.trim() !== "") : [],
          savedAirlineToUpdate: selectedAgentId,
        }
        : {}),
    };

    if (hasAnyErrors()) {
      toast.error("Please fix the highlighted errors before saving.");
      return;
    }

    try {
      const resp = await updateApi(payload);
      // Surface API-level failures the axios layer leaves unthrown (controllers return
      // 400 with status:400 in the body for validation errors instead of throwing).
      if (resp && Number(resp.status) >= 400) {
        console.error("❌ Save returned error status:", resp);
        toast.error(resp.message || resp.error || "Failed to save rates.");
        return;
      }
      setFqctTrigger((prev) => prev + 1);

      setIsEditing((prev) => ({
        ...prev,
        [selectedAgentId]: false,
      }));
      toast.success(isAirline ? "Rates saved" : "Charges saved");
    } catch (err) {
      console.error("❌ Failed to update all:", err);
      toast.error(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to save. Please try again."
      );
    }
  };

  // Handle save
  const handleFinalRateSubmit = async (rate, index) => {
    const payload = {
      token,
      quoteId,
      role: role,
      savedAgentToUpdate: selectedAgentId,
      finalRate: Number(rate.currentRate) || 0,
      minCharge: Number(rate.minCurrentRate) || 0,
      screeningPrices: {
        primaryScreeningPrice: {
          ratePerVolumeWeight: Number(rate.PrimaryScreening) || 0,
          minCharge: Number(rate.minPrimaryScreening) || 0,
        },
        secondaryScreeningPrice: {
          ratePerVolumeWeight: Number(rate.SecondaryScreening) || 0,
          minCharge: Number(rate.minSecondaryScreening) || 0,
        },
        destinationAirport: rate.DestinationAirport || "",
      },
      transitTime: rate.transitTime || "",
      ...(isAirline
        ? {
          FFWH: {
            fuelSurcharge: Number(rate.Fuel) || 0,
            freightCharge: {
              ratePerVolumeWeight: Number(rate.currentRate) || 0,
              minCharge: Number(rate.minCurrentRate) || 0,
            },
            warRiskSurcharge: {
              ratePerVolumeWeight: Number(rate.War) || 0,
              minCharge: Number(rate.minWar) || 0,
            },
            handlingFee: {
              ratePerVolumeWeight: Number(rate.Handling) || 0,
              minCharge: Number(rate.minHandling) || 0,
            },
          },
          originAirports: Array.isArray(rate.OriginAirports) ? rate.OriginAirports.filter(airport => airport && airport.trim() !== "") : [],
          savedAirlineToUpdate: selectedAgentId,
        }
        : {}),
    };

    // Pre-flight guards — the backend rejects with 400 if any of these are
    // missing, and the resulting toast used to be a generic "try again"
    // which hid the real cause (often: the dropdown still shows "Select
    // your airline/transporter" placeholder and selectedAgentId is null
    // because the user cleared/never picked one). We also reject anything
    // that isn't a 24-char hex string — Mongo ObjectIds are the only thing
    // the backend will accept, and an upstream bug once left this as the
    // literal object `{}`, which slipped past `!selectedAgentId` and gave
    // a confusing "Invalid SavedAirlineId format." from the server.
    const isValidObjectId =
      typeof selectedAgentId === "string" && /^[a-f\d]{24}$/i.test(selectedAgentId);
    if (!selectedAgentId || !isValidObjectId) {
      toast.error(
        isAirline
          ? "Pick an airline before saving rates."
          : "Pick a transporter before saving rates."
      );
      return;
    }
    if (!quoteId) {
      toast.error(
        "Quote ID is missing — refresh the page and try again."
      );
      return;
    }
    if (!(Number(rate.currentRate) > 0)) {
      toast.error("Final rate must be greater than 0.");
      return;
    }

    if (hasAnyErrors()) {
      toast.error("Please fix the highlighted errors before saving.");
      return;
    }

    try {
      const resp = await updateApi(payload);
      // Controllers return 400 with `{ status: 400, message, error }` in
      // the response body for validation errors instead of throwing — the
      // axios layer leaves those un-thrown, so we have to inspect.
      if (resp && Number(resp.status) >= 400) {
        console.error("❌ Save returned error status:", resp);
        toast.error(resp.error || resp.message || "Failed to update final rate.");
        return;
      }
      setFqctTrigger((prev) => prev + 1);

      setIsEditing((prev) => {
        if (Array.isArray(agentRatesRaw)) {
          return {
            ...prev,
            [selectedAgentId]: {
              ...(prev[selectedAgentId] || {}),
              [index]: false,
            },
          };
        }
        return { ...prev, [selectedAgentId]: false };
      });
      toast.success(isAirline ? "Airline rate saved" : "Transporter rate saved");
    } catch (err) {
      console.error("❌ Failed to update final rate:", err);
      // Surface the underlying server error so the next typo / missing-id
      // case is one toast away from diagnosis instead of "try again".
      toast.error(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update final rate. Please try again."
      );
    }
  };

  // Edit mode toggle
  const handleEdit = (index) => {
    setIsEditing((prev) => {
      if (Array.isArray(agentRatesRaw)) {
        return {
          ...prev,
          [selectedAgentId]: {
            ...(prev[selectedAgentId] || {}),
            [index]: true,
          },
        };
      }
      return { ...prev, [selectedAgentId]: true };
    });
  };

  // Add new destination box
  const handleAddDestination = () => {
    setFinalRates((prev) => {
      const existing = prev[selectedAgentId];
      const newEntry = {
        PrimaryScreening: Number(existing?.PrimaryScreening) || 0,
        minPrimaryScreening: Number(existing?.minPrimaryScreening) || 0,
        SecondaryScreening: Number(existing?.SecondaryScreening) || 0,
        minSecondaryScreening: Number(existing?.minSecondaryScreening) || 0,
        DestinationAirport: existing?.DestinationAirport || "",
      };
      if (existing?.screeningPrices) {
        return {
          ...prev,
          [selectedAgentId]: { ...existing, screeningPrices: [...existing.screeningPrices, newEntry] },
        };
      } else {
        return {
          ...prev,
          [selectedAgentId]: {
            ...existing, screeningPrices: [{
              PrimaryScreening: Number(existing?.PrimaryScreening) || 0,
              minPrimaryScreening: Number(existing?.minPrimaryScreening) || 0,
              SecondaryScreening: Number(existing?.SecondaryScreening) || 0,
              minSecondaryScreening: Number(existing?.minSecondaryScreening) || 0,
              DestinationAirport: existing?.DestinationAirport || "",
            }, newEntry]
          },
        };
      }
    });
  };

  const handleAddOriginAirport = () => {
    setFinalRates((prev) => {
      const updatedRates = { ...prev };
      const agentRates = updatedRates[selectedAgentId];
      if (agentRates) {
        const currentAirports = Array.isArray(agentRates.OriginAirports) ? agentRates.OriginAirports : [];
        updatedRates[selectedAgentId] = {
          ...agentRates,
          OriginAirports: [...currentAirports, ""]
        };
      }
      return updatedRates;
    });
  };

  const handleDeleteOriginAirport = (idx) => {
    setFinalRates((prev) => {
      const updatedRates = { ...prev };
      const agentRates = updatedRates[selectedAgentId];
      if (agentRates && Array.isArray(agentRates.OriginAirports)) {
        const updatedOriginAirports = agentRates.OriginAirports.filter((_, index) => index !== idx);
        updatedRates[selectedAgentId] = {
          ...agentRates,
          OriginAirports: updatedOriginAirports
        };
      }
      return updatedRates;
    });
  }

  return (
    <div className="space-y-3 pb-16">
      <div className="border-b border-slate-100 px-4 py-3 dark:border-[oklch(0.3_0.02_264/0.6)]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-fk-accent dark:bg-blue-500/15 dark:text-blue-300">
              <FontAwesomeIcon icon={isAirline ? faPlane : faTruck} className="text-[14px]" />
            </span>
            <div className="min-w-0">
              <h3 className="text-[15px] font-bold tracking-tight text-slate-900 dark:text-white">
                {isAirline ? "Airline Rates" : "Transporter Charges"}
              </h3>
              <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                {hasSelection
                  ? `${activeRateCount} ${activeRateCount === 1 ? "entry" : "entries"}`
                  : "Select a partner to enter rates"}
              </p>
            </div>
          </div>
          {hasSelection && (() => {
            // Three honest states, where there used to be two: an untouched card
            // is neither being edited nor saved, and must not wear a green tick.
            const editing = isEditing[selectedAgentId];
            const tone = editing
              ? "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300"
              : hasAnyRateEntered
                ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300"
                : "bg-slate-100 text-slate-500 dark:bg-slate-500/10 dark:text-slate-400";
            return (
              <span
                data-testid="rate-card-status"
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${tone}`}
                title={
                  editing || hasAnyRateEntered
                    ? undefined
                    : "Nothing has been entered on this card yet"
                }
              >
                {(editing || hasAnyRateEntered) && (
                  <FontAwesomeIcon
                    icon={editing ? faEdit : faCheck}
                    className="text-[9px]"
                  />
                )}
                {editing ? "Editing" : hasAnyRateEntered ? "Saved" : "No rates yet"}
              </span>
            );
          })()}
        </div>
      </div>

      {agentRates.map((rate, index) => {
        const editable = isEditing[selectedAgentId];
        const RateContentForTransporter = () => (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3">
            <RateEditorField title="Final Rate (£)">
              <input
                type="number"
                value={rate.currentRate || ""}
                onChange={(e) => handleRateChange(e, "currentRate", index)}
                disabled={!editable}
                placeholder={editable ? "0.00" : "Not set"}
                className={inputCls(index, "currentRate")}
              />
              {getError(index, "currentRate") && (
                <p className="text-[10px] text-red-500">{getError(index, "currentRate")}</p>
              )}
            </RateEditorField>

            <RateEditorField title="Min Freight (£)">
              <input
                type="number"
                value={rate.minCurrentRate || ""}
                onChange={(e) => handleRateChange(e, "minCurrentRate", index)}
                disabled={!editable}
                placeholder={editable ? "0.00" : "Not set"}
                className={inputCls(index, "minCurrentRate")}
              />
              {getError(index, "minCurrentRate") && (
                <p className="text-[10px] text-red-500">{getError(index, "minCurrentRate")}</p>
              )}
            </RateEditorField>
          </div>
        );
        const isLastT = index === (finalRates[selectedAgentId]?.screeningPrices?.length ?? 0) - 1;
        // One entry = no accordion: a tab already scopes this content, so a
        // second collapse layer is pure nesting (design feedback). Multiple
        // destination entries keep per-entry collapse.
        const flatEntry = agentRates.length === 1;
        const entryOpen = flatEntry || collapsedStates[index] === false;

        const isDisabled = !selectedAgentId || !isEditing[selectedAgentId];

        return (
          <div key={index} className="space-y-4">
            {!isAirline && index === 0 && RateContentForTransporter()}
            <div
              className={`rounded-xl border border-slate-200/80 bg-white dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)] ${entryOpen ? "overflow-visible" : "overflow-hidden"
                }`}
            >
              <button
                type="button"
                onClick={() => {
                  if (flatEntry) return;
                  setCollapsedStates(prev => ({
                    ...prev,
                    [index]: !(prev[index] ?? true)
                  }));
                }}
                className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left ${flatEntry ? "cursor-default" : ""}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-200">
                    {isAirline ? "Rates & Surcharges" : `Destination ${rate.DestinationAirport || index + 1}`}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${editable ? "bg-emerald-50 text-emerald-600 border border-emerald-200 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300" : "bg-slate-50 text-slate-500 border border-slate-200 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-400"}`}>
                    {editable ? "Editing" : "Saved"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <AnimatePresence>
                    {isLastT && (
                      <motion.button
                        key="delete-rate-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFinalRates((prev) => {
                            const existing = prev[selectedAgentId];
                            if (existing?.screeningPrices) {
                              return {
                                ...prev,
                                [selectedAgentId]: {
                                  ...existing,
                                  screeningPrices: existing.screeningPrices.filter((_, i) => i !== index)
                                }
                              };
                            }
                            return prev;
                          });
                        }}
                        disabled={isDisabled}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-500 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Remove this rate entry"
                        initial={{ opacity: 0, scale: 0.8, x: 10 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.8, x: 10 }}
                        transition={{
                          duration: 0.5,
                          ease: 'easeOut'
                        }}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-[11px]" />
                      </motion.button>
                    )}
                  </AnimatePresence>
                  <motion.button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) return;
                      if (isLastT) {
                        handleAddDestination();
                      } else {
                        setFinalRates((prev) => {
                          const existing = prev[selectedAgentId];
                          if (!existing?.screeningPrices) return prev;
                          return {
                            ...prev,
                            [selectedAgentId]: {
                              ...existing,
                              screeningPrices: existing.screeningPrices.filter((_, i) => i !== index)
                            }
                          };
                        });
                      }
                    }}
                    disabled={isDisabled}
                    title={isLastT ? 'Add destination' : 'Remove this rate entry'}
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${isLastT
                        ? "border-emerald-100 bg-emerald-50 text-emerald-600"
                        : "border-rose-100 bg-rose-50 text-rose-500"
                      }`}
                    whileHover={
                      isDisabled
                        ? {}
                        : {
                          scale: 1.08,
                        }
                    }
                    whileTap={isDisabled ? {} : { scale: 0.9 }}
                  >
                    <FontAwesomeIcon icon={isLastT ? faPlus : faTimes} className="text-[11px]" />
                  </motion.button>
                  {!flatEntry && (
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`text-[10px] text-slate-400 transition-transform duration-200 ${collapsedStates[index] !== false ? "rotate-180" : "rotate-0"
                        }`}
                    />
                  )}
                </div>
              </button>

              {/* Grid — always open for a single entry, collapsible for multi */}
              {entryOpen && (
                <div className="border-t border-slate-100 px-4 py-3 pb-5 dark:border-[oklch(0.3_0.02_264/0.6)]">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {isAirline && index === 0 && (
                      <>
                        <p className="col-span-2 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500" style={{ fontFamily: RATE_MONO_FONT }}>FREIGHT</p>
                        <RateEditorField title="Freight Rate (£/kg)">
                          <input
                            type="number"
                            value={rate.currentRate || ""}
                            onChange={(e) => handleRateChange(e, "currentRate", index)}
                            disabled={!editable}
                            placeholder={editable ? "0.00" : "Not set"}
                            className={inputCls(index, "currentRate")}
                          />
                          {getError(index, "currentRate") && (
                            <p className="text-[10px] text-red-500">{getError(index, "currentRate")}</p>
                          )}
                        </RateEditorField>
                        <RateEditorField title="Min Freight Charge (£)">
                          <input
                            type="number"
                            value={rate.minCurrentRate || ""}
                            onChange={(e) => handleRateChange(e, "minCurrentRate", index)}
                            disabled={!editable}
                            placeholder={editable ? "0.00" : "Not set"}
                            className={inputCls(index, "minCurrentRate")}
                          />
                          {getError(index, "minCurrentRate") && (
                            <p className="text-[10px] text-red-500">{getError(index, "minCurrentRate")}</p>
                          )}
                        </RateEditorField>
                      </>
                    )}

                    <p className="col-span-2 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500" style={{ fontFamily: RATE_MONO_FONT }}>SCREENING</p>

                    <RateEditorField title={isAirline ? "Primary Screening (£/kg)" : "Primary Screening (£)"}>
                      <input
                        type="number"
                        value={rate.PrimaryScreening || ""}
                        onChange={(e) => handleRateChange(e, "PrimaryScreening", index)}
                        disabled={!editable}
                        placeholder={editable ? "0.00" : "Not set"}
                        className={inputCls(index, "PrimaryScreening")}
                      />
                      {getError(index, "PrimaryScreening") && (
                        <p className="text-[10px] text-red-500">{getError(index, "PrimaryScreening")}</p>
                      )}
                      <input
                        type="number"
                        value={rate.minPrimaryScreening || ""}
                        onChange={(e) => handleRateChange(e, "minPrimaryScreening", index)}
                        disabled={!editable}
                        placeholder={editable ? "Minimum (£)" : "Not set"}
                        className={inputCls(index, "minPrimaryScreening")}
                      />
                      {getError(index, "minPrimaryScreening") && (
                        <p className="text-[10px] text-red-500">{getError(index, "minPrimaryScreening")}</p>
                      )}
                    </RateEditorField>

                    <RateEditorField title={isAirline ? "Secondary Screening (£/kg)" : "Secondary Screening (£)"}>
                      <input
                        type="number"
                        value={rate.SecondaryScreening || ""}
                        onChange={(e) => handleRateChange(e, "SecondaryScreening", index)}
                        disabled={!editable}
                        placeholder={editable ? "0.00" : "Not set"}
                        className={inputCls(index, "SecondaryScreening")}
                      />
                      {getError(index, "SecondaryScreening") && (
                        <p className="text-[10px] text-red-500">{getError(index, "SecondaryScreening")}</p>
                      )}
                      <input
                        type="number"
                        value={rate.minSecondaryScreening || ""}
                        onChange={(e) => handleRateChange(e, "minSecondaryScreening", index)}
                        disabled={!editable}
                        placeholder={editable ? "Minimum (£)" : "Not set"}
                        className={inputCls(index, "minSecondaryScreening")}
                      />
                      {getError(index, "minSecondaryScreening") && (
                        <p className="text-[10px] text-red-500">{getError(index, "minSecondaryScreening")}</p>
                      )}
                    </RateEditorField>

                    {isAirline ? (
                      <>
                        <p className="col-span-2 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500" style={{ fontFamily: RATE_MONO_FONT }}>SURCHARGES</p>

                        <RateEditorField title="Fuel Surcharge (%)" className="col-span-2">
                          <input
                            type="number"
                            value={rate.Fuel || ""}
                            onChange={(e) => handleRateChange(e, "Fuel", index)}
                            disabled={!editable}
                            placeholder={editable ? "0" : "Not set"}
                            className={inputCls(index, "Fuel")}
                          />
                          {getError(index, "Fuel") && (
                            <p className="text-[10px] text-red-500">{getError(index, "Fuel")}</p>
                          )}
                        </RateEditorField>

                        <RateEditorField title="War Risk Surcharge (£/kg)">
                          <input
                            type="number"
                            value={rate.War || ""}
                            onChange={(e) => handleRateChange(e, "War", index)}
                            disabled={!editable}
                            placeholder={editable ? "0.00" : "Not set"}
                            className={inputCls(index, "War")}
                          />
                          {getError(index, "War") && (
                            <p className="text-[10px] text-red-500">{getError(index, "War")}</p>
                          )}
                          <input
                            type="number"
                            value={rate.minWar || ""}
                            onChange={(e) => handleRateChange(e, "minWar", index)}
                            disabled={!editable}
                            placeholder={editable ? "Minimum (£)" : "Not set"}
                            className={inputCls(index, "minWar")}
                          />
                          {getError(index, "minWar") && (
                            <p className="text-[10px] text-red-500">{getError(index, "minWar")}</p>
                          )}
                        </RateEditorField>
                        <RateEditorField title="Handling Fee (£/kg)">
                          <input
                            type="number"
                            value={rate.Handling || ""}
                            onChange={(e) => handleRateChange(e, "Handling", index)}
                            disabled={!editable}
                            placeholder={editable ? "0.00" : "Not set"}
                            className={inputCls(index, "Handling")}
                          />
                          {getError(index, "Handling") && (
                            <p className="text-[10px] text-red-500">{getError(index, "Handling")}</p>
                          )}
                          <input
                            type="number"
                            value={rate.minHandling || ""}
                            onChange={(e) => handleRateChange(e, "minHandling", index)}
                            disabled={!editable}
                            placeholder={editable ? "Minimum (£)" : "Not set"}
                            className={inputCls(index, "minHandling")}
                          />
                          {getError(index, "minHandling") && (
                            <p className="text-[10px] text-red-500">{getError(index, "minHandling")}</p>
                          )}
                        </RateEditorField>

                        <p className="col-span-2 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500" style={{ fontFamily: RATE_MONO_FONT }}>ROUTE</p>

                        <RateEditorField title="Origin Airports" className="col-span-2">
                          <div className="space-y-1.5">
                            {(Array.isArray(rate.OriginAirports) ? rate.OriginAirports : []).map((airport, idx) => {
                              const isLast = idx === rate.OriginAirports.length - 1;
                              return (
                                <div key={idx} className="flex items-center gap-1.5">
                                  <div className="relative flex-1">
                                    <input
                                      type="text"
                                      value={airport || ""}
                                      onChange={(e) => handleRateChange(e, "OriginAirports", index, idx)}
                                      disabled={!editable}
                                      placeholder="Airport code (e.g., LHR)"
                                      className={RATE_EDITOR_INPUT_CLASS}
                                    />
                                    {airport && (
                                      <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded text-emerald-600 bg-emerald-50">
                                          {airport.length > 2 ? '✓' : '2+'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <motion.button
                                    type="button"
                                    onClick={() =>
                                      isLast
                                        ? handleAddOriginAirport()
                                        : handleDeleteOriginAirport(idx)
                                    }
                                    disabled={!editable}
                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${isLast
                                        ? "border-blue-100 bg-blue-50 text-blue-600"
                                        : "border-rose-100 bg-rose-50 text-rose-500"
                                      }`}
                                    title={isLast ? 'Add airport' : 'Remove airport'}
                                    whileHover={{ scale: 1.08 }}
                                    whileTap={{ scale: 0.9 }}
                                  >
                                    <FontAwesomeIcon
                                      icon={isLast ? faPlus : faTimes}
                                      className="text-[11px]"
                                    />
                                  </motion.button>
                                </div>
                              )
                            })}
                            {(!Array.isArray(rate.OriginAirports) || rate.OriginAirports.length === 0) && (
                              <div className="flex items-center gap-1.5">
                                <div className="flex-1 rounded-md border border-dashed border-slate-200 bg-white px-2.5 py-1.5 text-center text-[11px] text-slate-400 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)]">
                                  No airports added yet
                                </div>
                                {editable && (
                                  <motion.button
                                    type="button"
                                    onClick={() => handleAddOriginAirport()}
                                    className="inline-flex h-7 items-center gap-1 rounded-md bg-blue-600 px-2.5 text-[11px] font-semibold text-white transition hover:bg-blue-700"
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
                                    Add
                                  </motion.button>
                                )}
                              </div>
                            )}
                          </div>
                        </RateEditorField>

                        <RateEditorField title="Transit Time (in Days)">
                          <input
                            type="text"
                            value={(rate?.transitTime == undefined || rate?.transitTime == "undefined") ? " " : rate?.transitTime}
                            onChange={(e) => handleRateChange(e, "transitTime", index)}
                            disabled={!editable}
                            placeholder="3 - 4 days"
                            className={RATE_EDITOR_INPUT_CLASS}
                          />
                        </RateEditorField>
                      </>
                    ) : (
                      <RateEditorField title="Departure Airport">
                        <input
                          type="text"
                          value={rate.DestinationAirport || ""}
                          onChange={(e) => handleRateChange(e, "DestinationAirport", index)}
                          disabled={!editable}
                          placeholder="Departure Airport"
                          className={RATE_EDITOR_INPUT_CLASS}
                        />
                      </RateEditorField>
                    )}

                    {/* Action Buttons */}
                    <div className="col-span-2 mt-1.5 flex items-center justify-end border-t border-slate-100 pt-3 dark:border-[oklch(0.3_0.02_264/0.6)]">
                      {isAirline && <div className="flex gap-2 justify-end w-full">
                        {editable ? (() => {
                          const canSave =
                            rate.currentRate > 0 &&
                            typeof selectedAgentId === "string" &&
                            /^[a-f\d]{24}$/i.test(selectedAgentId);
                          const saveTitle = !selectedAgentId || typeof selectedAgentId !== "string"
                            ? "Pick an airline first"
                            : !(rate.currentRate > 0)
                              ? "Enter a final rate greater than 0"
                              : "Save rates";
                          return (
                            <motion.button
                              type="button"
                              onClick={() => handleFinalRateSubmit(rate, index)}
                              disabled={!canSave}
                              title={saveTitle}
                              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[12.5px] font-bold transition ${canSave
                                  ? "bg-fk-accent text-white shadow-[0_8px_18px_-8px_rgba(37,99,235,0.6)] hover:bg-blue-700"
                                  : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-500"
                                } disabled:cursor-not-allowed disabled:opacity-50`}
                              whileHover={canSave ? { scale: 1.03 } : {}}
                              whileTap={canSave ? { scale: 0.95 } : {}}
                            >
                              <FontAwesomeIcon icon={faSave} className="text-[11px]" />
                              Save charges
                            </motion.button>
                          );
                        })() : (
                          <motion.button
                            type="button"
                            onClick={() => handleEdit(index)}
                            className="inline-flex items-center gap-2 rounded-xl bg-fk-accent px-4 py-2.5 text-[12.5px] font-bold text-white shadow-[0_8px_18px_-8px_rgba(37,99,235,0.6)] transition hover:bg-blue-700"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-[11px]" />
                            Edit charges
                          </motion.button>
                        )}
                      </div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Add button */}
      {!isAirline && <div className="flex justify-end items-center gap-2 mt-3">
        <div className="flex justify-end">
          {isEditing[selectedAgentId] ? (
            <motion.button
              type="button"
              onClick={handleSaveAll}
              disabled={agentRates.length === 0}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12px] font-semibold transition ${agentRates.length > 0
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "cursor-not-allowed bg-slate-100 text-slate-400"
                } disabled:cursor-not-allowed disabled:opacity-50`}
              whileHover={agentRates.length > 0 ? { scale: 1.03 } : {}}
              whileTap={agentRates.length > 0 ? { scale: 0.95 } : {}}
            >
              <FontAwesomeIcon icon={faSave} className="text-[11px]" />
              Save Charges
            </motion.button>
          ) : (
            <motion.button
              type="button"
              onClick={() =>
                setIsEditing((prev) => ({ ...prev, [selectedAgentId]: true }))
              }
              disabled={!selectedAgentId || !isSavable}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12px] font-semibold transition ${!selectedAgentId || !isSavable
                  ? "cursor-not-allowed bg-slate-100 text-slate-400"
                  : "bg-amber-500 text-white hover:bg-amber-600"
                } disabled:cursor-not-allowed disabled:opacity-50`}
              whileHover={!selectedAgentId || !isSavable ? {} : { scale: 1.03 }}
              whileTap={!selectedAgentId || !isSavable ? {} : { scale: 0.95 }}
            >
              <FontAwesomeIcon icon={faEdit} className="text-[11px]" />
              Edit Charges
            </motion.button>
          )}
        </div>
      </div>
      }
    </div>
  );
}

export default function TransportConversation({
  my_saved_agents = [],
  setPanelTriggerKey,
  disabled,
  USER_EMAIL,
  defaultOpen,
  openPanel,
  setRefetchTrigger,
  setFqctTrigger = setRefetchTrigger,
  panelTriggerKey,
  onFullscreen,
  isFullscreen,
  finalAgentId,
  initialSectionTab = "messages",
  fillParentHeight = true,
  chargesOnly = false,
}) {
  const [selectedAgentId, setSelectedAgentId] = useState(
    finalAgentId ? finalAgentId :
      openPanel?.transport?.selectedAgentId ||
      (typeof openPanel["transport"] === 'string' ? openPanel["transport"] : null) ||
      my_saved_agents?.[0]?.agentId ||
      ""
  );
  const [agentInput, setAgentInput] = useState("");
  const [showAgentSuggestions, setShowAgentSuggestions] = useState(false);
  const [agents, setAgents] = useState([]);
  const [newAgent, setNewAgent] = useState({
    // "name": "",
    // "airlineCode": "",
    // "email_address": "",
    // "description": ""
  });

  const [finalRates, setFinalRates] = useState({
    ...my_saved_agents.map(agent => ({
      [agent.agentId]: {
        currentRate: Number(agent.finalRate) || 0,
        minCurrentRate: Number(agent.minCharge) || 0,
        PrimaryScreening: Number(agent?.screeningPrices?.primaryScreeningPrice?.ratePerVolumeWeight || agent?.screeningPrices?.primaryScreeningPrice) || 0,
        minPrimaryScreening: Number(agent?.screeningPrices?.primaryScreeningPrice?.minCharge) || 0,
        SecondaryScreening: Number(agent?.screeningPrices?.secondaryScreeningPrice?.ratePerVolumeWeight || agent?.screeningPrices?.secondaryScreeningPrice) || 0,
        minSecondaryScreening: Number(agent?.screeningPrices?.secondaryScreeningPrice?.minCharge) || 0,
        DestinationAirport: agent?.screeningPrices?.destinationAirport || "",
        OriginAirports: Array.isArray(agent?.OriginAirports) ? agent.OriginAirports : [],
        Fuel: Number(agent?.FFWH?.fuelSurcharge) || 0,
        Freight: Number(agent?.FFWH?.freightCharge?.ratePerVolumeWeight) || 0,
        War: Number(agent?.FFWH?.warRiskSurcharge?.ratePerVolumeWeight) || 0,
        minWar: Number(agent?.FFWH?.warRiskSurcharge?.minCharge) || 0,
        Handling: Number(agent?.FFWH?.handlingFee?.ratePerVolumeWeight) || 0,
        minHandling: Number(agent?.FFWH?.handlingFee?.minCharge) || 0,
        transitTime: agent?.transitTime || "",
        ...(agent?.screeningPrices?.length > 0 ? {
          screeningPrices: agent.screeningPrices.map(price => ({
            PrimaryScreening: Number(price?.primaryScreeningPrice?.ratePerVolumeWeight || price?.primaryScreeningPrice) || 0,
            minPrimaryScreening: Number(price?.primaryScreeningPrice?.minCharge) || 0,
            SecondaryScreening: Number(price?.secondaryScreeningPrice?.ratePerVolumeWeight || price?.secondaryScreeningPrice) || 0,
            minSecondaryScreening: Number(price?.secondaryScreeningPrice?.minCharge) || 0,
            DestinationAirport: price?.destinationAirport || "",
          }))
        } : {})
      }
    })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
  });

  const [isEditing, setIsEditing] = useState({});
  const [emailMessages, setEmailMessages] = useState([]);
  /* Guards the thread against showing the WRONG conversation. Switching
     transporter fires a new fetch while the previous one may still be in
     flight; without a generation token a slow earlier response can land after
     the newer one and repaint the previous transporter's messages. */
  const threadReqRef = useRef(0);
  const [TO_EMAIL, setTO_EMAIL] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [threadRefreshLoading, setThreadRefreshLoading] = useState(false);
  const [threadRefreshCooldown, setThreadRefreshCooldown] = useState(false);
  const [scrollOnUpdate, setScrollOnUpdate] = useState(false);
  const [threadId, setThreadId] = useState()

  /** charges = partner + rates UI (scrollable); messages = template + thread (full height) */
  const [sectionTab, setSectionTab] = useState(
    chargesOnly
      ? "charges"
      : initialSectionTab === "charges" || initialSectionTab === "messages"
        ? initialSectionTab
        : "messages"
  );

  useEffect(() => {
    /** Steps 10-11 of quote-detail tour land in the transporter lane. See quoteDetailTour.js. */
    const onTourStep = (e) => {
      const { tourId, step } = e.detail || {};
      if (tourId !== "quote-detail" || typeof step !== "number") return;
      if (chargesOnly) return;
      if (step === 10) setSectionTab("charges"); // Transporter lane intro
      else if (step === 11) setSectionTab("charges"); // Enter transporter rate
    };
    window.addEventListener("fk:tour:step", onTourStep);
    return () => window.removeEventListener("fk:tour:step", onTourStep);
  }, [chargesOnly]);

  const [initialEmail, setInitialEmail] = useState("");
  const [initialSubject, setInitialSubject] = useState("");


  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const quoteId = useParams().id;

  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateData, setTemplateData] = useState({});
  const [templatePopulating, setTemplatePopulating] = useState(false);
  const replyDraftDirtyRef = useRef(false);
  const onReplyDraftDirty = useCallback((dirty) => {
    replyDraftDirtyRef.current = dirty;
  }, []);
  const [templateSwitchModalOpen, setTemplateSwitchModalOpen] = useState(false);
  const [pendingTemplateName, setPendingTemplateName] = useState(null);
  const pendingTemplateExtraRef = useRef([]);

  const my_saved_agents_filtered = (my_saved_agents || []).filter(a => {
    const savedName = (a?.agentDetail?.name || a?.agentId || "").toLowerCase();
    const input = (agentInput || "").toLowerCase();
    return savedName.includes(input);
  });

  const filteredAgents = (agents || []).filter(a => {
    const agentName = (a?.name || a?._id || "").toLowerCase();
    const input = (agentInput || "").toLowerCase();

    return (
      agentName.includes(input) &&
      !my_saved_agents_filtered.some(saved => {
        const savedName = (saved?.agentDetail?.name || saved?.agentId || "").toLowerCase();
        return savedName === agentName;
      })
    );
  });

  /** Master agents (Admin directory), excluding any already on this quote — same idea as legacy typeahead without text filter. */
  const directoryAgentsForPartnerPicker = (agents || []).filter((a) => {
    const id = a?._id;
    if (!id) return false;
    if ((my_saved_agents || []).some((s) => s.agentId === id)) return false;
    const nameLower = (a.name || "").trim().toLowerCase();
    if (
      nameLower &&
      (my_saved_agents || []).some(
        (s) =>
          (s.agentDetail?.name || s.agentId || "").toLowerCase() === nameLower
      )
    ) {
      return false;
    }
    return true;
  });

  const allAgentCodes = agents.map(a => a.agentCode);

  const handleTemplateChange = (event, availableTemplates2 = []) => {
    const selectedName = event.target.value;
    if (selectedName === selectedTemplateName) return;
    const template = [...availableTemplates, ...availableTemplates2].find(
      (t) => t.templateName === selectedName
    );
    if (replyDraftDirtyRef.current) {
      pendingTemplateExtraRef.current = availableTemplates2;
      setPendingTemplateName(selectedName);
      setTemplateSwitchModalOpen(true);
      return;
    }
    setSelectedTemplateName(selectedName);
    if (template) fetchTemplateContent(template);
  };

  const confirmPendingTemplateSwitch = () => {
    const name = pendingTemplateName;
    const extra = pendingTemplateExtraRef.current;
    setPendingTemplateName(null);
    pendingTemplateExtraRef.current = [];
    setTemplateSwitchModalOpen(false);
    if (!name) return;
    setSelectedTemplateName(name);
    const t = [...availableTemplates, ...extra].find((x) => x.templateName === name);
    if (t) fetchTemplateContent(t);
  };

  const cancelPendingTemplateSwitch = () => {
    setPendingTemplateName(null);
    pendingTemplateExtraRef.current = [];
    setTemplateSwitchModalOpen(false);
  };

  const DataForCalculation = openPanel?.transport?.dataForCalculation || {};

  const bottomRef = useRef(null);

  const selectedAgent = my_saved_agents.find((a) => a.agentId === selectedAgentId);
  const selectedAgentMaster = (agents || []).find((a) => a._id === selectedAgentId);
  const resolvedAgentToEmail = (
    selectedAgent?.agentDetail?.email_address ||
    selectedAgentMaster?.email_address ||
    ""
  ).trim();

  // Subscribe to SSE-delivered live messages for ALL saved-agent threads on this
  // quote (not just the selected one) so each agent pane can pulse when a new
  // message arrives in its thread, even when another agent is currently open.
  const activeThreadId = selectedAgent?.emailThreadId || threadId;
  const watchedThreadIds = useMemo(
    () =>
      (my_saved_agents || [])
        .map((a) => a.emailThreadId)
        .filter(Boolean),
    [my_saved_agents]
  );
  const liveMessagesByThread = useSelector((state) => {
    const map = state.threads.liveMessages || {};
    const result = {};
    for (const tid of watchedThreadIds) {
      if (map[tid]) result[tid] = map[tid];
    }
    if (activeThreadId && map[activeThreadId] && !result[activeThreadId]) {
      result[activeThreadId] = map[activeThreadId];
    }
    return result;
  }, shallowEqual);
  const liveMessages = activeThreadId
    ? liveMessagesByThread[activeThreadId] || []
    : [];
  const combinedTransportMessages = activeThreadId
    ? sortThreadMessages([...emailMessages, ...liveMessages])
    : emailMessages;

  // Per-agent pulse state — keyed by agentId. Auto-clears 2.8s after a new SSE
  // message lands in a thread the operator isn't currently looking at.
  const [agentPulseMap, setAgentPulseMap] = useState({});
  const prevLiveLensRef = useRef({});
  const pulseTimersRef = useRef({});
  useEffect(() => {
    const prev = prevLiveLensRef.current;
    const next = { ...prev };
    (my_saved_agents || []).forEach((a) => {
      const tid = a.emailThreadId;
      if (!tid) return;
      const len = (liveMessagesByThread[tid] || []).length;
      const prevLen = prev[tid] || 0;
      next[tid] = len;
      if (len > prevLen && a.agentId !== selectedAgentId) {
        const id = a.agentId;
        setAgentPulseMap((p) => ({ ...p, [id]: true }));
        if (pulseTimersRef.current[id]) clearTimeout(pulseTimersRef.current[id]);
        pulseTimersRef.current[id] = setTimeout(() => {
          setAgentPulseMap((p) => {
            const cp = { ...p };
            delete cp[id];
            return cp;
          });
        }, 2800);
      }
    });
    prevLiveLensRef.current = next;
  }, [liveMessagesByThread, my_saved_agents, selectedAgentId]);
  useEffect(
    () => () => {
      Object.values(pulseTimersRef.current).forEach((t) => clearTimeout(t));
      pulseTimersRef.current = {};
    },
    []
  );
  // Selecting an agent (via any path — partner menu, openPanel, etc.) clears
  // its pending pulse so the indicator doesn't linger after the operator has
  // already navigated to the thread.
  useEffect(() => {
    if (!selectedAgentId) return;
    setAgentPulseMap((p) => {
      if (!p[selectedAgentId]) return p;
      const cp = { ...p };
      delete cp[selectedAgentId];
      return cp;
    });
    if (pulseTimersRef.current[selectedAgentId]) {
      clearTimeout(pulseTimersRef.current[selectedAgentId]);
      delete pulseTimersRef.current[selectedAgentId];
    }
  }, [selectedAgentId]);
  const anyAgentPulse = Object.values(agentPulseMap).some(Boolean);

  // ---------------------------------------------------------------------------
  // Per-message AI assist + Extract-rates popover. Replaces the old top-of-thread
  // "Parse w/ AI" header button + AgentAiSummaryPanel pair: operator clicks the
  // wand on the agent reply they want to extract, the popover offers Summary /
  // Suggested reply (shared shape with customer flow) plus an "Extract rates"
  // tab that calls parseAgentRateFromInput and applies via updateSavedAgentFinalRate.
  // ---------------------------------------------------------------------------
  const [assistOpenForMessageId, setAssistOpenForMessageId] = useState(null);
  const [assistAnchorRect, setAssistAnchorRect] = useState(null);
  const [assistLoading, setAssistLoading] = useState(false);
  const [assistError, setAssistError] = useState(null);
  const [assistData, setAssistData] = useState({
    summary: null,
    suggestedReply: null,
    cached: false,
    generatedAt: null,
  });
  const [extractLoading, setExtractLoading] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [applying, setApplying] = useState(false);

  const closeAssist = useCallback(() => {
    setAssistOpenForMessageId(null);
    setAssistAnchorRect(null);
    setAssistError(null);
    setAssistLoading(false);
    setExtractLoading(false);
    setExtractError(null);
    setExtractedData(null);
    setApplying(false);
  }, []);

  const handleAssistRegenerate = useCallback(async () => {
    if (!assistOpenForMessageId) return;
    if (!quoteId || !loginToken || !userLoggedIn?.role) return;
    setAssistLoading(true);
    setAssistError(null);
    try {
      const res = await postQuoteMessageAssist({
        loginToken,
        role: userLoggedIn.role,
        quoteId,
        messageId: assistOpenForMessageId,
        threadId: selectedAgent?.emailThreadId || threadId,
        mode: "both",
        force: true,
      });
      setAssistData({
        summary: res.summary ?? null,
        suggestedReply: res.suggestedReply ?? null,
        cached: Boolean(res.cached),
        generatedAt:
          res.generatedAt != null
            ? new Date(res.generatedAt).toLocaleString()
            : null,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many assist requests. Try again in about a minute.");
        setAssistError("Too many requests. Please wait a minute.");
      } else {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Assist failed";
        setAssistError(typeof msg === "string" ? msg : "Assist failed");
      }
    } finally {
      setAssistLoading(false);
    }
  }, [
    assistOpenForMessageId,
    quoteId,
    loginToken,
    userLoggedIn?.role,
    selectedAgent?.emailThreadId,
    threadId,
  ]);

  const handleAssistCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  }, []);

  const handleMessageAssist = useCallback(
    async (messageId, anchorEl) => {
      const rect = anchorEl?.getBoundingClientRect?.();
      setAssistAnchorRect(
        rect
          ? {
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
          }
          : null
      );
      setAssistOpenForMessageId(messageId);
      setAssistError(null);
      setAssistData({
        summary: null,
        suggestedReply: null,
        cached: false,
        generatedAt: null,
      });
      setExtractError(null);
      setExtractedData(null);
      setExtractLoading(false);
      setApplying(false);

      if (!quoteId || !loginToken || !userLoggedIn?.role) {
        setAssistError("Missing quote or session");
        return;
      }
      setAssistLoading(true);
      try {
        const res = await postQuoteMessageAssist({
          loginToken,
          role: userLoggedIn.role,
          quoteId,
          messageId,
          threadId: selectedAgent?.emailThreadId || threadId,
          mode: "both",
          force: false,
        });
        setAssistData({
          summary: res.summary ?? null,
          suggestedReply: res.suggestedReply ?? null,
          cached: Boolean(res.cached),
          generatedAt:
            res.generatedAt != null
              ? new Date(res.generatedAt).toLocaleString()
              : null,
        });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 429) {
          toast.error("Too many assist requests. Try again in about a minute.");
          setAssistError("Too many requests. Please wait a minute.");
        } else {
          const msg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Assist failed";
          setAssistError(typeof msg === "string" ? msg : "Assist failed");
        }
      } finally {
        setAssistLoading(false);
      }
    },
    [
      quoteId,
      loginToken,
      userLoggedIn?.role,
      // selectedAgent.emailThreadId is needed inside the closure to route the
      // assist call to the right Gmail thread. Without it the callback
      // captures the value from first render (usually undefined) and the
      // backend falls back to q.gmailThreadId → message_not_found.
      selectedAgent?.emailThreadId,
      threadId,
    ]
  );

  const assistMessageText = useMemo(() => {
    if (!assistOpenForMessageId) return "";
    const msg = (combinedTransportMessages || []).find(
      (m) => (m.id || m.messageId) === assistOpenForMessageId
    );
    if (!msg) return "";
    return stripHtmlToText(msg.html || "");
  }, [assistOpenForMessageId, combinedTransportMessages]);

  const handleExtract = useCallback(async () => {
    if (!selectedAgent?._id) {
      toast.error("Select a transporter first.");
      return;
    }
    if (!assistMessageText) {
      toast.error("No message text available to extract from.");
      return;
    }
    setExtractError(null);
    setExtractedData(null);
    setExtractLoading(true);
    try {
      const res = await parseAgentRateFromInput({
        loginToken,
        role: userLoggedIn?.role,
        savedAgentId: selectedAgent._id,
        messageId: assistOpenForMessageId || undefined,
        text: assistMessageText,
        force: false,
      });
      if (res?.empty) {
        setExtractError(
          res?.message ||
          "AI didn't find rate values in this message. Try a different message in the thread."
        );
        setExtractedData(null);
      } else {
        setExtractedData(res?.data || null);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many parse requests. Try again in about a minute.");
        setExtractError("Too many requests. Please wait a minute.");
      } else {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Extract failed";
        setExtractError(typeof msg === "string" ? msg : "Extract failed");
      }
    } finally {
      setExtractLoading(false);
    }
  }, [
    selectedAgent?._id,
    assistMessageText,
    assistOpenForMessageId,
    loginToken,
    userLoggedIn?.role,
  ]);

  const handleApply = useCallback(
    async (parsed) => {
      if (!selectedAgent) {
        toast.error("Saved transporter not found.");
        return;
      }
      const data = parsed || extractedData || {};
      const sp = data?.screeningPrices || {};
      const finalRate = Number(data?.finalCost ?? 0) || 0;
      const minCharge =
        sp?.otherScreeningPrice?.min_charge != null
          ? Number(sp.otherScreeningPrice.min_charge) || 0
          : 0;

      // updateSavedAgentFinalRate accepts the screeningPrices structure as-is;
      // it expects primary/secondary/other entries with ratePerVolumeWeight +
      // minCharge. destinationAirport is not part of that signature so we drop it.
      const screeningPricesBody = {
        primaryScreeningPrice: {
          ratePerVolumeWeight: Number(sp.primaryScreeningPrice?.per_kg ?? 0) || 0,
          minCharge: Number(sp.primaryScreeningPrice?.min_charge ?? 0) || 0,
        },
        secondaryScreeningPrice: {
          ratePerVolumeWeight: Number(sp.secondaryScreeningPrice?.per_kg ?? 0) || 0,
          minCharge: Number(sp.secondaryScreeningPrice?.min_charge ?? 0) || 0,
        },
        otherScreeningPrice: {
          ratePerVolumeWeight: Number(sp.otherScreeningPrice?.per_kg ?? 0) || 0,
          minCharge: Number(sp.otherScreeningPrice?.min_charge ?? 0) || 0,
        },
      };

      const body = {
        token: loginToken,
        role: userLoggedIn?.role,
        quoteId: selectedAgent.quoteId || quoteId,
        savedAgentToUpdate: selectedAgent.agentId,
        finalRate,
        minCharge,
        screeningPrices: screeningPricesBody,
      };

      setApplying(true);
      try {
        await updateSavedAgentFinalRate(body);
        toast.success("Transporter rate updated from AI extract.");
        // Mirror the freshly-applied values into the local finalRates state
        // (same reason as in AirlineConversation: finalRates was seeded once
        // from props and doesn't re-sync from refetched my_saved_agents).
        const agentKey = selectedAgent.agentId;
        const screening = body.screeningPrices || {};
        setFinalRates((prev) => ({
          ...(prev || {}),
          [agentKey]: {
            ...(prev?.[agentKey] || {}),
            currentRate: Number(body.finalRate) || 0,
            minCurrentRate: Number(body.minCharge) || 0,
            PrimaryScreening: Number(
              screening.primaryScreeningPrice?.ratePerVolumeWeight ?? 0
            ),
            minPrimaryScreening: Number(
              screening.primaryScreeningPrice?.minCharge ?? 0
            ),
            SecondaryScreening: Number(
              screening.secondaryScreeningPrice?.ratePerVolumeWeight ?? 0
            ),
            minSecondaryScreening: Number(
              screening.secondaryScreeningPrice?.minCharge ?? 0
            ),
          },
        }));
        if (typeof setFqctTrigger === "function") {
          setFqctTrigger((p) => p + 1);
        }
        closeAssist();
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to apply extracted rate";
        toast.error(typeof msg === "string" ? msg : "Failed to apply extracted rate");
      } finally {
        setApplying(false);
      }
    },
    [
      selectedAgent,
      extractedData,
      loginToken,
      userLoggedIn?.role,
      quoteId,
      setFqctTrigger,
      closeAssist,
      setFinalRates,
    ]
  );

  // Close popover when operator switches transporter.
  useEffect(() => {
    closeAssist();
  }, [selectedAgentId, closeAssist]);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useLayoutEffect(() => {
    if (scrollOnUpdate && !disabled) {
      isOpen && scrollToBottom();
      setScrollOnUpdate(false);
    }
  }, [emailMessages, scrollOnUpdate, isOpen]);

  const fetchAndSetThread = useCallback(
    async (customThreadId = null, opts = {}) => {
      const quiet = Boolean(opts?.quiet);
      const effectiveThreadId =
        customThreadId || selectedAgent?.emailThreadId || threadId;

      if (!effectiveThreadId) {
        if (!quiet) setEmailMessages([]);
        return;
      }

      const reqId = ++threadReqRef.current;

      if (quiet) setThreadRefreshLoading(true);
      else {
        setLoading(true);
        setErrorMsg("");
      }

      try {
        const res = await fetchEmailThread({
          token: loginToken,
          threadId: effectiveThreadId,
          userEmail: USER_EMAIL,
          role: userLoggedIn?.role,
        });

        // A newer selection superseded this request while it was in flight.
        if (reqId !== threadReqRef.current) return;
        setTO_EMAIL(resolvedAgentToEmail);
        const parsed = (res?.messages || []).map(parseEmailMessage);
        setEmailMessages(parsed);
        if (parsed.length > 0) {
          setSelectedTemplateName("");
          setTemplateData({});
        }
        // Auto-scroll disabled as latest messages are shown first
        setLastUpdated(new Date());
      } catch (err) {
        console.error("Failed to fetch thread:", err);
        const detail =
          err?.response?.data?.details ||
          err?.response?.data?.error ||
          err?.message ||
          "Unknown error";
        if (!quiet) {
          // Never leave the PREVIOUS selection's messages on screen under the
          // new one — a failed load read as "this transporter has that
          // conversation", which is worse than showing nothing.
          if (reqId === threadReqRef.current) setEmailMessages([]);
          setErrorMsg(`Failed to load conversation: ${detail}`);
        } else {
          throw err;
        }
      } finally {
        if (quiet) setThreadRefreshLoading(false);
        else setLoading(false);
      }
    },
    [loginToken, selectedAgent?.emailThreadId, threadId, resolvedAgentToEmail]
  );

  const handleManualThreadRefresh = useCallback(async () => {
    if (!selectedAgent?.emailThreadId && !threadId) return;
    if (threadRefreshLoading || threadRefreshCooldown) return;
    if (chargesOnly) return;
    try {
      await fetchAndSetThread(null, { quiet: true });
      setFqctTrigger?.((p) => p + 1);
      toast.success("Thread refreshed");
      setThreadRefreshCooldown(true);
      setTimeout(() => setThreadRefreshCooldown(false), 3000);
    } catch (e) {
      console.error("Could not refresh transport email thread", e);
      const detail =
        e?.response?.data?.details ||
        e?.response?.data?.error ||
        e?.message ||
        "Unknown error";
      toast.error(`Could not refresh thread: ${detail}`);
    }
  }, [
    selectedAgent?.emailThreadId,
    threadId,
    threadRefreshLoading,
    threadRefreshCooldown,
    chargesOnly,
    fetchAndSetThread,
    setFqctTrigger,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      let filteredTemplates
      let X = ["NS:", "QS:", "CS:"]
      let pattern = X.reduce((acc, x, i) => acc + (i > 0 ? "|" : "") + x, "");
      try {
        const AgentResponse = (await getAdminUsers({ token: loginToken, role: userLoggedIn?.role, limit: 1000, query: "agent" })).data;
        setAgents(AgentResponse?.agents || []);
        const AllTemplates = await getListOfTemplates({
          token: loginToken,
          role: userLoggedIn?.role,
          search: pattern,
          searchOnField: "templateName",
          role: userLoggedIn?.role,
          for: "transporter",
        });
        filteredTemplates = AllTemplates?.emailTemplates || [];
        setAvailableTemplates(filteredTemplates);
        if (openPanel?.transport?.selectTemplate) {
          applyForcedTemplate(
            openPanel.transport.selectTemplate,
            filteredTemplates
          );
        }
      } catch {
      } finally {
        setLoading(false);
      }
    };
    if (loginToken && quoteId) fetchData();
  }, [loginToken, quoteId, panelTriggerKey]);

  const applyForcedTemplate = (templatePattern, templates) => {
    const template = templates.find((t) => t.purpose === templatePattern);
    if (template) {
      setSelectedTemplateName(template.templateName);
      fetchTemplateContent(template);
    }
  };

  const fetchTemplateContent = async (template) => {
    if (!template || !template.purpose || !template.for) return;

    setTemplatePopulating(true);
    try {
      const templateResponse = await emailTemplatePopulated({
        token: loginToken,
        role: userLoggedIn?.role,
        quoteId,
        purpose: template.purpose,
        for: template.for,
      });
      let responseEmail = templateResponse?.emailContent || {};
      responseEmail.message = responseEmail.message.replace(/href="N\/A"/g, `href="${openPanel.transport.missingFields}"`);
      // if (responseEmail?.message?.includes("[Table]")) {
      //   const parts = responseEmail.message.split("[Table]");
      //   let tableHtml = "";
      //   if (typeof openPanel.transport.missingFields === "string") {
      //     tableHtml = openPanel.transport.missingFields;
      //   } else if (openPanel.transport.missingFields instanceof HTMLElement) {
      //     tableHtml = openPanel.transport.missingFields.outerHTML;
      //   } else {
      //     tableHtml = "";
      //   }
      //   setTable(tableHtml ? tableHtml : false)
      //   responseEmail.message = `${parts[0]} [NON-EDITABLE TABLE ATTACHMENT SHOWN EXTERNALLY] ${parts[1] ?? ""}`;
      // }

      // if (responseEmail?.message?.includes(["Cost_Breakdown"])) {
      //   const parts = responseEmail.message.split("[Cost_Breakdown]");
      //   responseEmail.message = `${parts[0]} ${renderCostBreakdown(DataForCalculation, "transport")} ${parts[1] ?? ""}`;
      // }
      if (responseEmail?.message?.includes(["Payable"])) {
        const parts = responseEmail.message.split("[Payable]");
        responseEmail.message = `${parts[0]} ${highlightedPayable(DataForCalculation, "transport")} ${parts[1] ?? ""}`;
      }
      if (emailMessages.length === 0) {
        setInitialSubject(responseEmail.subject);
        setInitialEmail(responseEmail.message);
      }
      setTemplateData(responseEmail);

    } catch (err) {
      console.error("Error fetching template content:", err);
    } finally {
      setTemplatePopulating(false);
    }
  };

  useEffect(() => {
    if (!availableTemplates.length) return;
    if (openPanel?.transport?.selectTemplate) return;
    if (selectedAgent?.emailThreadId || threadId) return;
    if (emailMessages.length > 0) return;
    if (selectedTemplateName) return;
    const first = availableTemplates[0];
    if (first?.templateName) {
      setSelectedTemplateName(first.templateName);
      fetchTemplateContent(first);
    }
  }, [
    availableTemplates,
    openPanel?.transport?.selectTemplate,
    selectedAgent?.emailThreadId,
    threadId,
    emailMessages.length,
    selectedTemplateName,
  ]);

  const handleInitialEmailSend = async (replyHtml) => {
    if (!initialEmail || !initialSubject) {
      setErrorMsg("Subject and message are required.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await sendEmail({
        token: loginToken,
        to: resolvedAgentToEmail || undefined,
        // TESTING ONLY — deliberately pinned so test sends can't leave this
        // inbox. Must become `USER_EMAIL` (the quote's receivedInInbox) before
        // production: a fixed sender mails one tenant's correspondence as
        // another (tracker G3). Note the send at the bottom of this file
        // already uses USER_EMAIL, so the two paths disagree until this is
        // reverted.
        from: "shawak@vantagecargo.com",
        selectedAgentId: selectedAgentId,
        subject: initialSubject,
        body: replyHtml,
        entityType: "agent",
        role: userLoggedIn?.role,
        // The transporter's NAME. Passing the entity type here is what produced
        // "Agent agent replied" in the bell: the notification text is built as
        // `${capitalize(entityType)} ${entityName}` (fetchDomainUsers.js:969).
        entityName:
          selectedAgent?.agentDetail?.name ||
          selectedAgentMaster?.name ||
          "Transporter",
        quoteId,
      });

      if (res?.threadId) {
        setThreadId(res.threadId)
        // The backend already persisted this thread onto the saved agent
        // (draftController -> agentUpdateThreadId), but this component's
        // my_saved_agents prop is now stale, so selectedAgent.emailThreadId is
        // still null locally. Refetch the quote so the agent carries its OWN
        // thread; without this the pane depends on the component-level
        // threadId, which is exactly what leaked into the next transporter.
        setRefetchTrigger?.((prev) => prev + 1);
        await fetchAndSetThread(res.threadId);
        setPanelTriggerKey((prev) => (prev + 1))
      } else {
        throw new Error("Thread ID not returned");
      }
    } catch (err) {
      console.error("❌ Failed to send initial email:", err);
      setErrorMsg("Failed to start conversation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async (replyHtml, attachments = [], recipients = {}) => {
    const latest = emailMessages?.[0];
    const allReferences = emailMessages.map((msg) => msg.messageId).filter(Boolean).reverse().join(" ");
    try {
      // Ensure 'to' is an array with at least one valid email
      const toRecipients = Array.isArray(recipients.to)
        ? recipients.to.filter(email => email && email.trim())
        : (recipients.to ? [recipients.to] : resolvedAgentToEmail ? [resolvedAgentToEmail] : []);

      if (toRecipients.length === 0) {
        throw new Error('At least one recipient is required');
      }

      await replyToEmailThread({
        token: loginToken,
        threadId: selectedAgent?.emailThreadId || threadId,
        to: toRecipients,
        cc: Array.isArray(recipients?.cc) ? recipients.cc.filter(e => e && e.trim()) : [],
        bcc: Array.isArray(recipients?.bcc) ? recipients.bcc.filter(e => e && e.trim()) : [],
        role: userLoggedIn?.role,
        subject: latest?.subject || null,
        body: replyHtml,
        from: USER_EMAIL,
        inReplyTo: latest?.messageId,
        references: allReferences,
        attachments,
      });
      await fetchAndSetThread();
    } catch (err) {
      console.error("❌ Failed to send reply", err);
      setErrorMsg("Failed to send email. Please try again.");
    }
  };

  // Auto-select agent when openPanel.transport.selectedAgentId is set (e.g., from notification click).
  // QuoteAllDetails populates `selectedAgentId` (matches the AirlineConversation pattern); the prior
  // `selectedTransportId` key never landed, so notification-opened transporter threads stayed empty.
  useEffect(() => {
    const incomingAgentId =
      openPanel?.transport?.selectedAgentId ||
      openPanel?.transport?.selectedTransportId;
    if (incomingAgentId && incomingAgentId !== selectedAgentId) {
      setSelectedAgentId(incomingAgentId);

      const matchingAgent = my_saved_agents.find(a => a.agentId === incomingAgentId);
      if (matchingAgent?.agentDetail?.name) {
        setAgentInput(matchingAgent.agentDetail.name);
      }
    }
  }, [
    openPanel?.transport?.selectedAgentId,
    openPanel?.transport?.selectedTransportId,
    my_saved_agents,
  ]);

  useEffect(() => {
    // Blank the thread the moment the selection changes, before the fetch
    // resolves. Otherwise the previous transporter\'s messages stay visible
    // under the new name for the whole debounce + round trip.
    setEmailMessages([]);
    setErrorMsg("");
    // Also drop the component-level threadId. fetchAndSetThread resolves
    // `customThreadId || selectedAgent?.emailThreadId || threadId`, and that last
    // fallback still held the PREVIOUS transporter\'s thread — so a transporter
    // with no conversation of its own would show someone else\'s, including a
    // bounce, and the composer would address it.
    setThreadId(null);
    if (!loginToken || !selectedAgent?.emailThreadId) {
      if (selectedAgent?.quoteId) {
        // Show add airline button
      }
      setEmailMessages([]);
      return
    };
    const timeout = setTimeout(() => {
      fetchAndSetThread();
    }, 300);
    return () => clearTimeout(timeout);
  }, [loginToken, selectedAgent?.emailThreadId]);

  // Register every saved-agent thread for SSE so the per-agent pulse keeps
  // working when the operator is parked on a different agent. Refires only
  // when the watched-thread list itself changes (not on every render).
  const watchedThreadKey = watchedThreadIds.join(",");
  useEffect(() => {
    if (!loginToken || !watchedThreadIds.length) return;
    watchedThreadIds.forEach((tid) => {
      registerWatchedThread({
        token: loginToken,
        threadId: tid,
        role: userLoggedIn?.role,
      }).catch((err) =>
        console.error("Failed to register transport thread for SSE:", err)
      );
    });
  }, [loginToken, watchedThreadKey, userLoggedIn?.role]);

  const createNewAgent = async (agentDataOverride) => {
    const agentToSave = agentDataOverride || newAgent;

    if (!agentToSave.name || !agentToSave.agentCode || !agentToSave.email_address) {
      showSwal("Failure", "Please provide airline name, unique code, and email.", 200);
      return null;
    }
    try {
      const res = await addAdmins({
        role: "agent",
        token: loginToken,
        role: userLoggedIn?.role,
        data: { ...agentToSave, role: "agent", agentType: "transport" }
      });
      showSwal("Success", res.message, 200);
      return res.data?.agents;
    } catch (err) {
      console.error("Failed to create airline", err);
      return null
    }
  };

  const saveMyNewAgentWithQuote = async (agent) => {
    const type = "transport";
    try {
      if (agent?.email_address && quoteId) {
        const payload = {
          "quoteId": quoteId,
          "agentId": agent._id,
          "richEmail": {
            "subject": "Request for new Rate",
            "body": "<p>Request for new Rate</p>"
          },
          "pricingCondition": "general"
        }
        const response = await saveDraftEmail({
          token: loginToken,
          quoteId: quoteId,
          agentId: agent._id,
          role: userLoggedIn?.role,
          [type === "agent" ? "saveAgents" : "saveAgents"]: [payload],
          type,
        });

        if (response.status === 200) {
          showSwal("Success", response.message || "Agent Saved Successfully", response.status);
        } else {
          showSwal("Failure", response.message || "Email Save Failure", "error");
        }
      }
    } catch (error) {
      console.error("Failed to save new Agent with quote", error);
      showSwal("Failure", "Failed to save new agent with quote. Please try again.", 200);
    }
  }

  /** Gates “Edit charges” / create actions: typeahead must match a known partner OR be empty. Also allow when the selected id is already a transporter on this quote (native select-only changes, stale agentInput after other flows). */
  const selectedTransporterOnQuote = (my_saved_agents || []).some(
    (a) => String(a.agentId) === String(selectedAgentId)
  );
  const isSavable =
    selectedTransporterOnQuote ||
    !(agentInput || "").trim() ||
    (my_saved_agents || []).some((a) => {
      const savedName = (a?.agentDetail?.name || a?.agentId || "").toLowerCase();
      return savedName === (agentInput || "").trim().toLowerCase();
    }) ||
    (agents || []).some((a) => {
      const name = (a?.name || a?._id || "").toLowerCase();
      return String(a._id) === String(selectedAgentId) && name === (agentInput || "").trim().toLowerCase();
    });

  const transportPartnerSelectorRef = useRef(null);
  const [transportPartnerMenuOpen, setTransportPartnerMenuOpen] = useState(false);
  const [transportPartnerCreateOpen, setTransportPartnerCreateOpen] = useState(false);
  const [transportPartnerSaving, setTransportPartnerSaving] = useState(false);

  useEffect(() => {
    if (!transportPartnerMenuOpen) return;
    const onPointerDown = (e) => {
      if (
        transportPartnerSelectorRef.current &&
        !transportPartnerSelectorRef.current.contains(e.target)
      ) {
        setTransportPartnerMenuOpen(false);
        setTransportPartnerCreateOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setTransportPartnerMenuOpen(false);
        setTransportPartnerCreateOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [transportPartnerMenuOpen]);

  const handleCreateAndSaveAgentToQuote = async () => {
    if (transportPartnerSaving) return;
    setTransportPartnerSaving(true);
    try {
      const agentName = (agentInput || "").trim().toLowerCase();
      const matchedAgent = (agents || []).find((a) => {
        const name = (a?.name || a?._id || "").toLowerCase();
        return name === agentName;
      });
      let TheAgentToSave = null;
      if (!matchedAgent) {
        const agentToSave = {
          ...newAgent,
          name: agentInput.trim(),
        };
        const myNewAgent = await createNewAgent(agentToSave);
        if (myNewAgent) {
          TheAgentToSave = myNewAgent[0];
          setAgents((prev) => [...prev, myNewAgent]);
        }
      } else {
        TheAgentToSave = matchedAgent;
      }
      if (TheAgentToSave) {
        await saveMyNewAgentWithQuote(TheAgentToSave);
        setNewAgent({ name: "", agentCode: "", email_address: "", description: "" });
        setAgentInput("");
        setRefetchTrigger((prev) => prev + 1);
        setSelectedAgentId(TheAgentToSave._id);
        setTransportPartnerMenuOpen(false);
        setTransportPartnerCreateOpen(false);
      } else {
        showSwal("Failure", "Failed to create agent. Please try again.", 400);
      }
    } catch (err) {
      console.error(err);
      showSwal("Failure", "Failed to save transporter. Please try again.", 400);
    } finally {
      setTransportPartnerSaving(false);
    }
  };

  const transportPartnerSwitchLocked = Boolean(
    selectedAgentId && isEditing[selectedAgentId]
  );

  const transportTemplateComposerAddon = (
    <div className="relative space-y-1.5">
      {templatePopulating ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm dark:bg-[oklch(0.225_0.026_264/0.75)]"
          aria-busy="true"
          aria-label="Loading template"
        >
          <div className="flex flex-col items-center gap-2">
            <AnimatedLoader variant="docbuild" size={72} label="Loading template" />
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Loading template…</span>
          </div>
        </div>
      ) : null}
      <label
        htmlFor="template-select-transport"
        className="block text-sm font-bold text-gray-700 dark:text-gray-200"
      >
        Select Email Template:
      </label>
      <select
        id="template-select-transport"
        className="w-full rounded border border-gray-300 bg-white px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none focus:shadow-outline dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-gray-200"
        value={selectedTemplateName}
        onChange={handleTemplateChange}
        disabled={loading || templatePopulating || availableTemplates.length === 0}
      >
        {loading ? (
          <option value="">Loading templates...</option>
        ) : availableTemplates.length === 0 ? (
          <option value="">No templates available</option>
        ) : (
          availableTemplates.map((template) => (
            <option key={template._id} value={template.templateName}>
              {formatTemplateOptionLabel(template)}
            </option>
          ))
        )}
      </select>
    </div>
  );

  return (
    <>
      <TemplateSwitchConfirmModal
        open={templateSwitchModalOpen}
        onConfirm={confirmPendingTemplateSwitch}
        onCancel={cancelPendingTemplateSwitch}
      />
      {errorMsg && (
        <div className="mb-2 shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {errorMsg}
        </div>
      )}
      <Accordion
        title={chargesOnly ? `${TRANSPORTER} rates` : `${TRANSPORTER} conversation`}
        disabled={disabled}
        setOpen={setIsOpen}
        defaultOpen={Boolean(defaultOpen) && !disabled}
        newMessageHeaderFlash={anyAgentPulse}
        for="transport"
        openPanel={openPanel}
        onFullscreen={onFullscreen}
        isFullscreen={isFullscreen}
        showFullscreenButton={true}
        tallBody
        fillParentHeight={fillParentHeight}
        headerAction={
          !chargesOnly && (selectedAgent?.emailThreadId || threadId) ? (
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={handleManualThreadRefresh}
                disabled={threadRefreshLoading || threadRefreshCooldown}
                title={
                  threadRefreshCooldown && !threadRefreshLoading
                    ? "Just refreshed"
                    : "Refresh thread"
                }
                aria-label="Refresh thread"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200/90 bg-slate-50/90 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.8)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
              >
                <FontAwesomeIcon
                  icon={threadRefreshLoading ? faCircleNotch : faRotateRight}
                  className={`text-[12px] ${threadRefreshLoading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          ) : null
        }
      >
        <div className="flex h-full min-h-0 min-w-0 flex-col">
          <div
            ref={transportPartnerSelectorRef}
            className="relative mb-2 w-full max-w-md shrink-0"
          >
            {finalAgentId ? (
              <div className="block w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-sm dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100">
                {
                  (my_saved_agents || []).filter((a) => a.agentId == finalAgentId)[0]
                    ?.agentDetail?.name
                }
              </div>
            ) : transportPartnerSwitchLocked ? (
              <div className="rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-base font-semibold text-slate-900 shadow-sm dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100">
                {selectedAgent?.agentDetail?.name ||
                  selectedAgentId ||
                  "No transporter selected"}
                <p className="mt-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                  Finish editing charges to switch transporter.
                </p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setTransportPartnerMenuOpen((o) => !o);
                    if (transportPartnerMenuOpen) setTransportPartnerCreateOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 rounded-[16px] border border-slate-200 bg-white py-3 pl-4 pr-3 text-left text-sm font-medium text-slate-800 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100 dark:focus:ring-blue-900/40"
                >
                  <span className="truncate">
                    {selectedAgent?.agentDetail?.name ||
                      (agents || []).find((x) => x._id === selectedAgentId)?.name ||
                      agentInput?.trim() ||
                      "Select your transporter"}
                  </span>
                  <svg
                    className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${transportPartnerMenuOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.185l3.71-3.955a.75.75 0 111.08 1.04l-4.25 4.53a.75.75 0 01-1.08 0l-4.25-4.53a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {transportPartnerMenuOpen && (
                  <div
                    className={`absolute left-0 right-0 z-50 mt-1 overflow-y-auto rounded-2xl border border-slate-200 bg-white py-1 shadow-xl dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] ${transportPartnerCreateOpen
                        ? "max-h-[min(36rem,55dvh)] [@media(max-height:720px)]:max-h-[min(24rem,48dvh)]"
                        : "max-h-[min(22rem,50dvh)] [@media(max-height:720px)]:max-h-[min(16rem,38dvh)]"
                      }`}
                  >
                    {transportPartnerCreateOpen ? (
                      <>
                        <button
                          type="button"
                          className="w-full px-4 py-2.5 text-left text-sm font-medium text-fk-accent hover:bg-slate-50 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                          onClick={() => setTransportPartnerCreateOpen(false)}
                        >
                          − Hide create form
                        </button>
                        <div className="space-y-3 border-t border-slate-200 bg-slate-50/95 p-3 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.6)]">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                              Transporter name
                            </label>
                            <input
                              type="text"
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100"
                              placeholder="Transporter name"
                              value={agentInput}
                              onChange={(e) => setAgentInput(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                              Agent email
                            </label>
                            <input
                              type="email"
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100"
                              placeholder="Enter agent email..."
                              value={newAgent.email_address || ""}
                              onChange={(e) =>
                                setNewAgent((prev) => ({
                                  ...prev,
                                  email_address: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                              Unique code
                            </label>
                            <input
                              type="text"
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100"
                              placeholder="Enter unique code..."
                              value={newAgent.agentCode || ""}
                              onChange={(e) => {
                                const code = e.target.value;
                                if (allAgentCodes.includes(code)) {
                                  setErrorMsg(
                                    `Agent code must be unique. "${code}" is already in use.`
                                  );
                                  setNewAgent((prev) => ({ ...prev, agentCode: "" }));
                                  return;
                                }
                                setNewAgent((prev) => ({ ...prev, agentCode: code }));
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-wait disabled:bg-slate-400 disabled:hover:bg-slate-400"
                            disabled={isSavable || transportPartnerSaving}
                            onClick={handleCreateAndSaveAgentToQuote}
                          >
                            {transportPartnerSaving && (
                              <svg
                                className="h-4 w-4 shrink-0 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            )}
                            {transportPartnerSaving
                              ? "Saving…"
                              : (agents || []).some((a) => {
                                const name = (a?.name || a?._id || "").toLowerCase();
                                return name === (agentInput || "").trim().toLowerCase();
                              })
                                ? "Save transporter to this quote"
                                : "Create & save new transporter to this quote"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {my_saved_agents.length === 0 &&
                          directoryAgentsForPartnerPicker.length === 0 && (
                            <p className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                              No transporters to choose from. Add transporters in Admin or use “Add /
                              create new transporter” below.
                            </p>
                          )}
                        {my_saved_agents.length > 0 && (
                          <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            On this quote
                          </p>
                        )}
                        {my_saved_agents.map((agent) => (
                          <button
                            key={agent.agentId}
                            type="button"
                            className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                            onClick={() => {
                              setAgentInput(agent.agentDetail?.name || "");
                              setSelectedAgentId(agent.agentId);
                              setAgentPulseMap((p) => {
                                const cp = { ...p };
                                delete cp[agent.agentId];
                                return cp;
                              });
                              if (pulseTimersRef.current[agent.agentId]) {
                                clearTimeout(pulseTimersRef.current[agent.agentId]);
                                delete pulseTimersRef.current[agent.agentId];
                              }
                              setTransportPartnerMenuOpen(false);
                              setTransportPartnerCreateOpen(false);
                            }}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">
                                {agent.agentDetail?.name || agent.agentId}
                              </span>
                              {(agent.agentDetail?.email_address ||
                                agent.agentDetail?.agentCode) && (
                                  <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                                    {[
                                      agent.agentDetail?.email_address,
                                      agent.agentDetail?.agentCode,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                )}
                            </span>
                            {agentPulseMap[agent.agentId] && (
                              <span
                                className="inline-flex h-2 w-2 shrink-0 animate-pulse rounded-full bg-blue-500"
                                aria-label="New message"
                                title="New message in this thread"
                              />
                            )}
                          </button>
                        ))}
                        {directoryAgentsForPartnerPicker.length > 0 && (
                          <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            All transporters (directory)
                          </p>
                        )}
                        {directoryAgentsForPartnerPicker.map((a) => (
                          <button
                            key={a._id}
                            type="button"
                            disabled={transportPartnerSaving}
                            className="w-full px-4 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60 dark:text-slate-100 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                            onClick={async () => {
                              // Picking from directory used to only set local state, leaving the user
                              // without a SavedAgent row — every rate save then 400'd with
                              // "quoteId, savedAgentToUpdate, and finalRate are required" because
                              // selectedAgent (looked up by agentId in my_saved_agents) was undefined.
                              // Auto-create the SavedAgent so rates can be edited immediately.
                              setAgentInput(a?.name || "");
                              setSelectedAgentId(a._id);
                              setTransportPartnerMenuOpen(false);
                              setTransportPartnerCreateOpen(false);
                              setTransportPartnerSaving(true);
                              try {
                                await saveMyNewAgentWithQuote(a);
                                setRefetchTrigger((prev) => prev + 1);
                              } catch (err) {
                                console.error(
                                  "Failed to attach directory transporter to quote:",
                                  err
                                );
                                toast.error(
                                  err?.response?.data?.message ||
                                  err?.message ||
                                  "Failed to attach transporter. Please try again."
                                );
                              } finally {
                                setTransportPartnerSaving(false);
                              }
                            }}
                          >
                            <span className="block font-medium">{a?.name || a._id}</span>
                            {(a.email_address || a.agentCode) && (
                              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                                {[a.email_address, a.agentCode].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </button>
                        ))}
                        <div className="border-t border-slate-200 dark:border-[oklch(0.4_0.02_264)]">
                          <button
                            type="button"
                            className="w-full px-4 py-2.5 text-left text-sm font-medium text-fk-accent hover:bg-slate-50 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
                            onClick={() => setTransportPartnerCreateOpen(true)}
                          >
                            + Add / create new transporter
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {!chargesOnly && (
            <div data-tour="transport-section-tabs" className="mb-2 flex shrink-0 gap-1 rounded-xl border border-slate-200/80 bg-slate-50/90 p-1 dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.225_0.026_264/0.5)]">
              <button
                type="button"
                onClick={() => setSectionTab("charges")}
                className={`flex-1 rounded-lg px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide transition-all sm:text-[12px] sm:normal-case sm:tracking-normal ${sectionTab === "charges"
                    ? "bg-blue-500/10 text-fk-accent shadow-sm ring-1 ring-blue-200/70 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30"
                    : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
              >
                Charges &amp; details
              </button>
              <button
                type="button"
                onClick={() => setSectionTab("messages")}
                className={`flex-1 rounded-lg px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide transition-all sm:text-[12px] sm:normal-case sm:tracking-normal ${sectionTab === "messages"
                    ? "bg-blue-500/10 text-fk-accent shadow-sm ring-1 ring-blue-200/70 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30"
                    : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
              >
                Messages
              </button>
            </div>
          )}
          {(chargesOnly || sectionTab === "charges") && (
            <div data-tour="transport-rates" className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto pr-0.5">
              {/* LEGACY: transporter typeahead + duplicate “Selected Transporter” card — superseded by unified selector above tabs; set to true to restore */}
              {false && (
                <div className="mb-4 flex flex-col justify-between">
                  {!finalAgentId && <div className="mb-4 flex-row">
                    <div className="flex items-center gap-x-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Transporter
                      </label>
                      <div className="relative w-64">
                        <input
                          type="text"
                          className="w-full p-2 border rounded-md text-gray-800 outline-none"
                          placeholder="Type to add or select transporter..."
                          value={agentInput}
                          onChange={(e) => {
                            setAgentInput(e.target.value);
                            setShowAgentSuggestions(true);
                          }}
                          onFocus={() => setShowAgentSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowAgentSuggestions(false), 200)}
                        />
                        {showAgentSuggestions && (filteredAgents.length > 0 || my_saved_agents_filtered.length > 0) && (
                          <div className="absolute top-full left-0 mt-1 w-full bg-white border rounded shadow z-50 max-h-60 overflow-y-auto">
                            <div className="border-b-2 border-gray-200 px-4 py-2 font-semibold">
                              {my_saved_agents_filtered.map((agent) => (
                                <div
                                  key={agent.agentId}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => {
                                    setAgentInput(agent.agentDetail?.name);
                                    setSelectedAgentId(agent.agentId);
                                    setShowAgentSuggestions(false);
                                  }}
                                >
                                  {agent.agentDetail?.name || agent.agentId}
                                </div>
                              ))}
                            </div>
                            <div>
                              {filteredAgents.map((agent) => (
                                <div
                                  key={agent._id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => {
                                    setAgentInput(agent?.name);
                                    setSelectedAgentId(agent._id);
                                    setShowAgentSuggestions(false);
                                  }}
                                >
                                  {agent?.name || agent._id}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {agentInput?.trim() &&
                      (my_saved_agents_filtered.length == 0 && filteredAgents.length == 0) && (
                        <>
                          <div className="flex gap-x-2">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Agent Email
                              </label>
                              <div className="relative w-64">
                                <input
                                  type="email"
                                  className="w-full p-2 border rounded-md text-gray-800 outline-none"
                                  placeholder="Enter agent email..."
                                  value={newAgent.email_address || ""}
                                  onChange={(e) => {
                                    const email = e.target.value;
                                    setNewAgent((prev) => ({ ...prev, email_address: email }));
                                  }}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Agent Unique Code
                              </label>
                              <div className="relative w-64">
                                <input
                                  type="text"
                                  className="w-full p-2 border rounded-md text-gray-800 outline-none"
                                  placeholder="Enter unique code..."
                                  value={newAgent.agentCode || ""}
                                  onChange={(e) => {
                                    const code = e.target.value;
                                    if (allAgentCodes.includes(code)) {
                                      setErrorMsg(
                                        `Agent code must be unique. "${code}" is already in use.`
                                      );
                                      setNewAgent((prev) => ({ ...prev, agentCode: "" }));
                                      return;
                                    }
                                    setNewAgent((prev) => ({ ...prev, agentCode: code }));
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                    <div className="flex items-center justify-start my-2 gap-x-2">
                      <button
                        className="px-3 py-1 rounded text-white disabled:bg-gray-400 bg-blue-600 hover:bg-blue-700"
                        onClick={async () => {
                          const agentName = (agentInput || "").trim().toLowerCase();
                          const matchedAgent = (agents || []).find((a) => {
                            const name = (a?.name || a?._id || "").toLowerCase();
                            return name === agentName;
                          });
                          let TheAgentToSave = null;
                          if (!matchedAgent) {
                            const agentToSave = {
                              ...newAgent,
                              name: agentInput.trim(),
                            };
                            const myNewAgent = await createNewAgent(agentToSave);
                            if (myNewAgent) {
                              TheAgentToSave = myNewAgent[0];
                              setAgents((prev) => [...prev, myNewAgent]);
                            }
                          } else {
                            TheAgentToSave = matchedAgent;
                          }
                          if (TheAgentToSave) {
                            await saveMyNewAgentWithQuote(TheAgentToSave);
                            setNewAgent({ name: "", agentCode: "", email_address: "", description: "" });
                            setAgentInput("");
                            setRefetchTrigger((prev) => prev + 1);
                            setSelectedAgentId(TheAgentToSave._id);
                          } else {
                            showSwal("Failure", "Failed to create agent. Please try again.", 400);
                          }
                        }}
                        disabled={isSavable}
                      >
                        {(agents || []).some((a) => {
                          const name = (a?.name || a?._id || "").toLowerCase();
                          return name === (agentInput || "").trim().toLowerCase();
                        })
                          ? "Save Agent to this Quote"
                          : "Create & Save new Agent to this Quote"}
                      </button>
                    </div>
                  </div>}
                  <div className="mb-2 w-full max-w-sm shrink-0">
                    {selectedAgentId && !isEditing[selectedAgentId] ? (
                      <div>
                        {!finalAgentId ? <select
                          value={selectedAgentId}
                          onChange={(e) => {
                            const id = e.target.value;
                            setSelectedAgentId(id);
                            const picked = (my_saved_agents || []).find((a) => String(a.agentId) === String(id));
                            if (picked?.agentDetail?.name) setAgentInput(picked.agentDetail.name);
                            else if (!id) setAgentInput("");
                          }}
                          className="block w-full max-w-sm rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                        >
                          <option key={null} value={""}>
                            Select Your Transporter
                          </option>
                          {my_saved_agents.map((agent) => (
                            <option key={agent.agentId} value={agent.agentId}>
                              {agent.agentDetail?.name || agent.agentId}
                            </option>
                          ))}
                        </select> : <div className="max-w-sm rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-sm">
                          {(my_saved_agents || []).filter(a => a.agentId == finalAgentId)[0]?.agentDetail?.name}
                        </div>}
                      </div>
                    ) : (
                      <div className="text-xl font-semibold text-slate-900">
                        {selectedAgent?.agentDetail?.name || selectedAgentId || "No transporter selected"}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <FinalRateSection
                selectedAgentId={selectedAgentId}
                openRates={openPanel?.transport?.openRates}
                isSavable={isSavable}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                finalRates={finalRates}
                setFinalRates={setFinalRates}
                // Fall back to the URL :id when no transporter is selected (same
                // reason as the airline-side fix in AirlineConversation:1899) —
                // FinalRateSection's preflight needs a quoteId from the moment
                // the user starts typing rates, not just after they pick a row.
                quoteId={selectedAgent?.quoteId || quoteId}
                token={loginToken}
                setRefetchTrigger={setRefetchTrigger}
                setFqctTrigger={setFqctTrigger}
                role={userLoggedIn?.role}
                updateApi={updateSavedAgentFinalRate}
                isAirline={false}
                hasSavedRates={recordHasRates(selectedAgent)}
              />
            </div>
          )}

          {!chargesOnly && sectionTab === "messages" && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {loading && (
                <div className="mb-3 flex shrink-0 items-center gap-2 rounded-md border border-violet-200/60 bg-violet-50/60 px-3 py-2 text-[12px] font-medium text-violet-700 dark:border-violet-500/30 dark:bg-violet-950/30 dark:text-violet-300">
                  <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-[11px]" />
                  Loading transporter conversation…
                </div>
              )}
              {loading && combinedTransportMessages.length === 0 ? (
                <EmailThreadSkeleton messages={5} />
              ) : (
                <>
                  <div className="mb-2 shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {combinedTransportMessages.length} message{combinedTransportMessages.length !== 1 && "s"}{" "}
                    {lastUpdated && `· Last updated ${lastUpdated.toLocaleTimeString()}`}
                  </div>

                  <EmailThread
                    toEmail={resolvedAgentToEmail || undefined}
                    recipientSyncKey={selectedAgentId || undefined}
                    messages={combinedTransportMessages}
                    threadId={selectedAgent?.emailThreadId || threadId}
                    onReplySubmit={(selectedAgent?.emailThreadId || threadId) ? handleReplySubmit : handleInitialEmailSend}
                    isInitialSubmitType={agentInput == "" ? selectedAgentId : agentInput == selectedAgent?.agentDetail?.name}
                    templateData={templateData}
                    USER_EMAIL={USER_EMAIL}
                    order="asc"
                    bottomRef={bottomRef}
                    variant="flat"
                    roomyComposer
                    naturalStackHeight={false}
                    composerAddon={transportTemplateComposerAddon}
                    conversationScrollKey={
                      selectedAgent?.emailThreadId || threadId || selectedAgentId || ""
                    }
                    onReplyDraftDirtyChange={onReplyDraftDirty}
                    collapsibleComposer
                    composerTourId="qd-reply-composer"
                    onMessageAssist={loginToken ? handleMessageAssist : undefined}
                    messageAssistInFlight={assistLoading}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Accordion>
      <AiMessageAssistPopover
        open={assistOpenForMessageId != null}
        anchorRect={assistAnchorRect}
        loading={assistLoading}
        error={assistError}
        data={assistData}
        onClose={closeAssist}
        onRegenerate={handleAssistRegenerate}
        onCopy={handleAssistCopy}
        kind="agent"
        onExtract={handleExtract}
        extractLoading={extractLoading}
        extractError={extractError}
        extractedData={extractedData}
        onApply={handleApply}
        applying={applying}
      />
    </>
  );
}
